"use client";
import { PhoneHeader } from "@/components/header/PhoneHeader";
import { EmptyHeart } from "@/components/newIcons/EmptyHeart";
import { User } from "@/components/newIcons/User";
import { getTypeTitle } from "@/lib/getTypeTitle";
import clsx from "clsx";
import Link from "next/link";
import { type RefObject, useEffect, useRef, useState } from "react";
import type { Departure } from "../../getDeparture";
import type { Travel } from "../../getTravel";

const HEADER_HEIGHT = 80;
const MENU_HEIGHT = 48;

type Props = {
  travel: Travel;
  departure: Departure | null;
  items: Array<{
    title: string;
    element: RefObject<HTMLDivElement | null>;
    scrollOffset: number;
  }>;
};
export const Menu = ({ travel, departure, items }: Props) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isMenuSticky, setIsMenuSticky] = useState(false);
  const isPremium = travel.brandCustomerTitle === "Hémérys";
  const [currentItem, setCurrentItem] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleScroll = () => {
      const fromTop = window.scrollY + HEADER_HEIGHT + MENU_HEIGHT + 1;
      const pass = items.filter(
        (item) =>
          item.element.current &&
          item.element.current.getBoundingClientRect().top + window.scrollY < fromTop + item.scrollOffset,
      );
      const cur = pass[pass.length - 1];
      setCurrentItem(cur?.title);

      if (window.scrollY > 175) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }

      if (menuRef.current) {
        if (menuRef.current.getBoundingClientRect().top - HEADER_HEIGHT <= 0) {
          setIsMenuSticky(true);
        } else {
          setIsMenuSticky(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [items]);

  const handleClick = ({ element, scrollOffset }: Props["items"][number]) => {
    if (element.current) {
      window.scrollTo({
        top: element.current.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT - MENU_HEIGHT - scrollOffset,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <div ref={menuRef} className="sticky top-20 h-12 z-39 hidden xl:block">
        <div
          className={clsx(
            "absolute h-12 w-screen top-0 right-full bg-linear-to-r from-transparent from-50% to-bg-2 to-50% bg-size-[200%_100%] transition-all pointer-events-none",
            {
              "bg-bottom-right": isMenuSticky,
              "bg-bottom-left": !isMenuSticky,
            },
          )}
        />
        <div
          className={clsx(
            "absolute h-12 w-screen top-0 left-full bg-linear-to-r from-bg-2 from-50% to-transparent to-50% bg-size-[200%_100%] transition-all pointer-events-none",
            {
              "bg-bottom-left": isMenuSticky,
              "bg-bottom-right": !isMenuSticky,
            },
          )}
        />
        <div
          className={clsx("flex bg-bg-2", {
            rounded: !isMenuSticky,
          })}
        >
          {items.map((item, index) => {
            const isActive = item.title === currentItem || (currentItem === undefined && index === 0);

            return (
              <div
                key={item.title}
                onClick={() => handleClick(item)}
                className={clsx(
                  "grow grid place-items-center text-center text-base/4 cursor-pointer h-12 px-2 xxl:px-5",
                  {
                    "font-semibold text-white bg-pr-1 rounded-sm hover:bg-[#C06847]": isActive,
                    "text-tx-1 hover:bg-bg-1": !isActive,
                  },
                )}
              >
                {item.title}
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={clsx("hidden xl:block xl:fixed xl:z-40 xl:top-0 xl:left-0 xl:right-0 transition-transform", {
          "-translate-y-full": !isSticky,
          "translate-y-0": isSticky,
          shadow: isSticky && !isMenuSticky,
        })}
      >
        <div
          className={clsx("h-20", {
            "bg-[#12100F]": isPremium,
            "bg-[#FBF7F4]": !isPremium,
          })}
        >
          <div className="max-w-(--breakpoint-xxl) mx-auto flex items-center h-full lg:px-10">
            <div className={isPremium ? "text-white" : "text-tx-1"}>
              <p
                className={clsx("text-lg leading-6 font-bold", {
                  "text-white": isPremium,
                  "text-tx-1": !isPremium,
                })}
              >
                {travel.title}
              </p>
              {travel.travelTypeId && departure && (
                <p className="text-sm">
                  {getTypeTitle(travel.travelTypeId)}
                  {travel.travelTypeId !== "sejour-un-jour" &&
                    ` ${departure.days || 0} jours / ${departure.nights || 0} nuit${(departure?.nights || 0) > 1 ? "s" : ""}`}
                </p>
              )}
            </div>
            <div className="grow" />
            <Link
              href={{
                pathname: "/account/liste-envies/",
              }}
              prefetch={false}
            >
              <EmptyHeart
                className={clsx("mr-5 cursor-pointer", {
                  "text-white": isPremium,
                  "text-pr-1": !isPremium,
                })}
              />
            </Link>
            <Link
              href={{
                pathname: "/account/",
              }}
              prefetch={false}
            >
              <User
                className={clsx("mr-5 cursor-pointer", {
                  "stroke-white": isPremium,
                  "stroke-pr-1": !isPremium,
                })}
              />
            </Link>
            <PhoneHeader isHeaderTransparent={false} />
          </div>
        </div>
      </div>
    </>
  );
};
